import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Languages, Quote, CheckCircle2 } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { SERVICES, ACCENTS } from "@/data/services";
import { MaskedLine, FadeIn, Reveal, Chapter } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import PhoneMockup from "@/components/PhoneMockup";
import BrowserFrame from "@/components/BrowserFrame";

const WHY = [
    { num: "01", title: "We build our own products", text: "LoadCircle and ServiceNext are live, revenue-ready products — designed, built, and shipped by this exact team.", accent: "bg-blue-500" },
    { num: "02", title: "One team, end to end", text: "Strategy, design, development, and marketing under one roof. No hand-offs, no finger-pointing.", accent: "bg-purple-500" },
    { num: "03", title: "Calgary local", text: "We know the local market because we live in it. Coffee meetings beat conference calls.", accent: "bg-teal-500" },
    { num: "04", title: "We speak your language", text: "English, Punjabi, and Hindi — clear communication from first call to final launch.", accent: "bg-amber-500" },
];

export default function Home() {
    useSEO(
        "Digital Circle | Websites, Apps & Marketing in Calgary",
        "Digital Circle is a Calgary digital agency building websites, web & mobile apps, branding, and ad campaigns that grow your business. Get a free quote."
    );

    const heroRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 40, damping: 18 });
    const sy = useSpring(my, { stiffness: 40, damping: 18 });
    const ringX = useTransform(sx, [-0.5, 0.5], [-45, 45]);
    const ringY = useTransform(sy, [-0.5, 0.5], [-45, 45]);

    const onMove = (e) => {
        const r = heroRef.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <main data-testid="home-page">
            {/* ---------- HERO ---------- */}
            <section
                ref={heroRef}
                onMouseMove={onMove}
                className="relative overflow-hidden pt-36 pb-20 md:pt-48 md:pb-28"
                data-testid="hero-section"
            >
                <motion.div
                    style={{ x: ringX, y: ringY }}
                    className="pointer-events-none absolute -top-48 right-[-18%] h-[620px] w-[620px] md:right-[-8%] md:h-[900px] md:w-[900px]"
                    aria-hidden="true"
                >
                    <div className="conic-ring spin-slow h-full w-full opacity-70" style={{ filter: "blur(26px)" }} />
                    <div className="conic-ring absolute inset-10 opacity-40 blur-3xl" />
                </motion.div>

                <div className="relative mx-auto max-w-7xl px-5 md:px-8">
                    <FadeIn delay={0}>
                        <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500" data-testid="hero-eyebrow">
                            <span className="ring-logo inline-block h-3.5 w-3.5" />
                            Digital Circle — Calgary, Alberta
                        </p>
                    </FadeIn>

                    <h1 className="mt-8 max-w-5xl text-5xl font-black leading-[1.02] tracking-tighter text-slate-900 sm:text-6xl lg:text-7xl xl:text-[5.5rem]" data-testid="hero-headline">
                        <MaskedLine delay={0.1}>We build</MaskedLine>
                        <MaskedLine delay={0.22}>
                            <span className="gradient-text">digital products</span>
                        </MaskedLine>
                        <MaskedLine delay={0.34}>that grow your business.</MaskedLine>
                    </h1>

                    <FadeIn delay={0.55} className="mt-8 max-w-xl">
                        <p className="text-base leading-relaxed text-slate-600 md:text-lg" data-testid="hero-subheading">
                            Websites, apps, and marketing — designed, built, and shipped by one Calgary team that has already launched products of its own.
                        </p>
                    </FadeIn>

                    <FadeIn delay={0.7} className="mt-10 flex flex-wrap items-center gap-4">
                        <Link
                            to="/contact"
                            data-testid="hero-quote-button"
                            className="group inline-flex items-center gap-2 rounded-full bg-slate-900 px-8 py-4 text-sm font-semibold text-white shadow-lg transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-slate-800 hover:shadow-xl"
                        >
                            Get a Free Quote
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <Link
                            to="/work"
                            data-testid="hero-work-button"
                            className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/70 px-8 py-4 text-sm font-semibold text-slate-900 backdrop-blur transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-slate-900 hover:shadow-lg"
                        >
                            See Our Work
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </FadeIn>

                    <FadeIn delay={0.85} className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-sm text-slate-500">
                        <span className="flex items-center gap-2" data-testid="hero-trust-location">
                            <MapPin className="h-4 w-4 text-rose-500" /> Based in Calgary, serving Canada
                        </span>
                        <span className="flex items-center gap-2" data-testid="hero-trust-languages">
                            <Languages className="h-4 w-4 text-blue-500" /> English · Punjabi · Hindi
                        </span>
                        <span className="flex items-center gap-2" data-testid="hero-trust-products">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Builders of LoadCircle & ServiceNext
                        </span>
                    </FadeIn>
                </div>
            </section>

            <EditorialMarquee />

            {/* ---------- SERVICES OVERVIEW ---------- */}
            <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="home-services-section">
                <Chapter index="01" label="What we do" />
                <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
                    <Reveal>
                        <h2 className="max-w-2xl text-4xl font-black tracking-tighter text-slate-900 md:text-5xl">
                            Everything your business needs to exist — and win — online.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link
                            to="/services"
                            data-testid="home-all-services-link"
                            className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-900"
                        >
                            All 11 services
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>

                <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {SERVICES.slice(0, 6).map((s, i) => {
                        const a = ACCENTS[s.accent];
                        return (
                            <Reveal key={s.slug} delay={i * 0.07}>
                                <Link
                                    to={`/contact?service=${encodeURIComponent(s.title)}`}
                                    data-testid={`home-service-card-${s.slug}`}
                                    className={`group block h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(15,23,42,0.18)] ${a.hoverBorder}`}
                                >
                                    <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${a.soft}`}>
                                        <s.icon className={`h-6 w-6 ${a.icon}`} />
                                    </span>
                                    <h3 className="mt-6 text-lg font-bold tracking-tight text-slate-900">{s.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.description}</p>
                                    <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 transition-colors duration-300 group-hover:text-slate-900">
                                        Request this service
                                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* ---------- FEATURED WORK ---------- */}
            <section className="bg-white py-24 md:py-32" data-testid="home-work-section">
                <div className="mx-auto max-w-7xl px-5 md:px-8">
                    <Chapter index="02" label="Proof, not promises" />
                    <Reveal className="mt-6">
                        <h2 className="max-w-3xl text-4xl font-black tracking-tighter text-slate-900 md:text-5xl">
                            We don't just build for clients. We build for ourselves.
                        </h2>
                    </Reveal>

                    <div className="mt-14 grid gap-10 lg:grid-cols-2">
                        <Reveal>
                            <Link to="/work" data-testid="home-work-loadcircle" className="group block">
                                <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-b from-slate-100 to-slate-50 p-6 md:p-10">
                                    <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-blue-200/50 blur-3xl" />
                                    <BrowserFrame
                                        src="/assets/loadcircle/hero-dashboard.png"
                                        alt="LoadCircle dashboard with live load tracking and profitability metrics"
                                        url="loadcircle.ca"
                                        className="relative transition-transform duration-500 group-hover:scale-[1.02]"
                                    />
                                </div>
                                <div className="mt-6 flex items-start justify-between gap-4 px-1">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-500">Web Platform · TMS</p>
                                        <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-slate-900">LoadCircle</h3>
                                        <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600">
                                            A Canadian-first transportation management system that ends paperwork chaos for carriers and freight brokers.
                                        </p>
                                    </div>
                                    <ArrowUpRight className="mt-2 h-6 w-6 shrink-0 text-slate-400 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-slate-900" />
                                </div>
                            </Link>
                        </Reveal>

                        <Reveal delay={0.12}>
                            <Link to="/work" data-testid="home-work-servicenext" className="group block">
                                <div className="relative overflow-hidden rounded-[2rem] bg-slate-950 p-6 md:p-10">
                                    <div className="absolute -left-16 -top-16 h-56 w-56 rounded-full bg-teal-500/25 blur-3xl" />
                                    <div className="absolute -bottom-20 -right-10 h-56 w-56 rounded-full bg-purple-500/25 blur-3xl" />
                                    <PhoneMockup className="relative transition-transform duration-500 group-hover:scale-[1.03]" />
                                </div>
                                <div className="mt-6 flex items-start justify-between gap-4 px-1">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-500">Mobile App · Marketplace</p>
                                        <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-slate-900">ServiceNext</h3>
                                        <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600">
                                            A marketplace app connecting customers with verified local service providers — ratings, response times, and instant chat.
                                        </p>
                                    </div>
                                    <ArrowUpRight className="mt-2 h-6 w-6 shrink-0 text-slate-400 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-slate-900" />
                                </div>
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ---------- WHY US ---------- */}
            <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="home-why-section">
                <Chapter index="03" label="Why Digital Circle" />
                <Reveal className="mt-6">
                    <h2 className="max-w-3xl text-4xl font-black tracking-tighter text-slate-900 md:text-5xl">
                        An agency that's also a product company.
                    </h2>
                </Reveal>

                <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
                    {WHY.map((w, i) => (
                        <Reveal key={w.num} delay={i * 0.08} data-testid={`why-${w.num}`}>
                            <div className={`h-1 w-10 rounded-full ${w.accent}`} />
                            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">{w.num}</p>
                            <h3 className="mt-3 text-xl font-bold tracking-tight text-slate-900">{w.title}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-600">{w.text}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ---------- TESTIMONIALS PLACEHOLDER ---------- */}
            <section className="bg-white py-24 md:py-32" data-testid="home-testimonials-section">
                <div className="mx-auto max-w-7xl px-5 md:px-8">
                    <Chapter index="04" label="Kind words" />
                    <Reveal className="mt-6">
                        <h2 className="text-4xl font-black tracking-tighter text-slate-900 md:text-5xl">
                            Client stories, coming soon.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-6 md:grid-cols-3">
                        {[1, 2, 3].map((n) => (
                            <Reveal key={n} delay={n * 0.08}>
                                <div
                                    data-testid={`testimonial-placeholder-${n}`}
                                    className="flex h-full min-h-[220px] flex-col justify-between rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-8"
                                >
                                    <Quote className="h-8 w-8 text-slate-300" />
                                    <p className="mt-8 text-sm leading-relaxed text-slate-400">
                                        A client story will live here soon — yours could be the first.
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- CTA BANNER ---------- */}
            <section className="mx-auto max-w-7xl px-5 pb-24 md:px-8 md:pb-32" data-testid="home-cta-section">
                <Reveal>
                    <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 px-8 py-16 md:px-16 md:py-24">
                        <div className="conic-ring spin-slow absolute -right-32 -top-32 h-[420px] w-[420px] opacity-30 blur-2xl" aria-hidden="true" />
                        <div className="relative flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">Ready when you are</p>
                                <h2 className="mt-4 max-w-2xl text-4xl font-black tracking-tighter text-white md:text-6xl">
                                    Your project could be <span className="gradient-text">next.</span>
                                </h2>
                                <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400">
                                    Tell us what you're building. We'll reply within one business day with an honest plan and a clear price.
                                </p>
                            </div>
                            <Link
                                to="/contact"
                                data-testid="cta-quote-button"
                                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-slate-900 shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-2xl"
                            >
                                Get a Free Quote
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
